import React, { useEffect, useRef, useState } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../../utils/axiosConfig';
import { useAuth } from '../../context/AuthContext';
import {
    Avatar, Cargando, DIAS_LETRA, Flotante, ESTADOS, MESES, capital, diaSemana, display, fechaCorta, fechaLarga, horaAmPm,
    horas, iso, minutosTexto, partesHora,
} from './ui';
import { resumenJornada } from './formato';

const hoyIso = () => iso(new Date());
const mesDe = (texto) => {
    const [a, m] = texto.split('-').map(Number);
    return new Date(a, m - 1, 1);
};
const mesTexto = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;

const Dato = ({ titulo, valor, detalle, tono = 'text-slate-900' }) => (
    <div className="rounded-xl bg-slate-50 px-3.5 py-3">
        <p className="text-xs text-slate-500">{titulo}</p>
        <p className={`${display} text-xl font-semibold tabular-nums mt-0.5 ${tono}`}>{valor}</p>
        {detalle && <p className="text-xs text-slate-400 mt-0.5">{detalle}</p>}
    </div>
);

// Un día del calendario de la persona
const Dia = ({ dia }) => {
    const estilo = ESTADOS[dia.estado] || ESTADOS.libre;
    const fecha = new Date(`${dia.fecha}T12:00:00`);
    const entrada = partesHora(dia.entrada);
    const salida = partesHora(dia.salida);
    const fondo = {
        ausente: 'bg-rose-50', justificada: 'bg-emerald-50', libre: 'bg-slate-50 rayado', en_curso: 'bg-cyan-50',
    }[dia.estado] || 'bg-white';
    return (
        <div
            className={`relative h-[74px] rounded-lg border border-slate-200/70 p-1.5 overflow-hidden ${fondo} ${dia.fecha === hoyIso() ? 'ring-2 ring-cyan-400' : ''}`}
            title={[fechaLarga(dia.fecha), estilo.nombre, dia.justificacion, dia.aviso].filter(Boolean).join('\n')}
        >
            <span className={`text-[11px] font-semibold tabular-nums ${dia.estado === 'libre' ? 'text-slate-400' : 'text-slate-700'}`}>{fecha.getDate()}</span>
            {entrada && (
                <p className="text-[10px] leading-tight tabular-nums text-slate-700 mt-0.5">
                    {entrada.hora} <span className="text-slate-400">{entrada.sufijo}</span>
                    {salida && <><br />{salida.hora} <span className="text-slate-400">{salida.sufijo}</span></>}
                </p>
            )}
            {dia.extra_min > 0 && (
                <p className="text-[9px] font-semibold leading-tight text-cyan-700 tabular-nums whitespace-nowrap">+{minutosTexto(dia.extra_min)}</p>
            )}
            {!entrada && dia.estado === 'ausente' && <p className="text-[10px] font-medium text-rose-600 mt-1">Faltó</p>}
            {!entrada && dia.estado === 'justificada' && <p className="text-[10px] font-medium text-emerald-700 mt-1 leading-tight line-clamp-2">{dia.justificacion}</p>}
            {!entrada && dia.estado === 'libre' && dia.justificacion && <p className="text-[10px] text-slate-500 mt-1 leading-tight line-clamp-2">{dia.justificacion}</p>}
            {dia.estado !== 'libre' && <span className={`absolute left-1.5 right-1.5 bottom-1.5 h-1 rounded-full ${estilo.punto}`} />}
            {dia.retardo_min > 0 && <span className="absolute top-1.5 right-1.5 text-[9px] font-semibold text-amber-600 tabular-nums">+{dia.retardo_min}</span>}
            {dia.aviso && <span className="absolute top-2 right-1.5 w-1.5 h-1.5 rounded-full bg-rose-500" />}
        </div>
    );
};

// La hora de Permisos va de 15 en 15 minutos: la llegada se redondea hacia arriba y la salida hacia abajo
const cuarto = (hhmm, arriba) => {
    const [h, m] = hhmm.slice(0, 5).split(':').map(Number);
    const total = arriba ? Math.ceil((h * 60 + m) / 15) * 15 : Math.floor((h * 60 + m) / 15) * 15;
    return `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
};

// Para justificar una novedad con un permiso que carga GH (Permisos -> Cargar permiso, ya llenado)
const justificacion = (d) => {
    if (d.estado === 'ausente') return { forma: 'un_dia' };
    if (d.estado === 'retardo' && d.horario_entrada && d.entrada) return { forma: 'horas', desde: d.horario_entrada, hasta: cuarto(d.entrada, true) };
    if (d.estado === 'salida_temprana' && d.horario_salida && d.salida) return { forma: 'horas', desde: cuarto(d.salida, false), hasta: d.horario_salida };
    return null;
};

const FichaPersona = ({ id, mesInicial, onCerrar }) => {
    const reducir = useReducedMotion();
    const navigate = useNavigate();
    const { user } = useAuth();
    // Solo GH carga permisos por otra persona
    const puedeJustificar = user?.role === 'gh';
    const justificar = (d) => {
        const j = justificacion(d);
        const params = new URLSearchParams({ cargar: String(id), fecha: d.fecha, forma: j.forma });
        if (j.desde) params.set('desde', j.desde);
        if (j.hasta) params.set('hasta', j.hasta);
        navigate(`/solicitud-permisos?${params.toString()}`);
    };
    const [mes, setMes] = useState(mesInicial || mesTexto(new Date()));
    const [datos, setDatos] = useState(null);
    const [error, setError] = useState(null);
    const cerrarRef = useRef(null);
    const onCerrarRef = useRef(onCerrar);
    onCerrarRef.current = onCerrar;

    useEffect(() => {
        let vivo = true;
        setDatos((d) => (d && d.persona.empleado_id === id ? { ...d, cargando: true } : null));
        axiosInstance.get(`/api/asistencia/personas/${id}`, { params: { mes } })
            .then(({ data }) => { if (vivo) { setDatos(data); setError(null); } })
            .catch(() => vivo && setError('No se pudo cargar la ficha.'));
        return () => { vivo = false; };
    }, [id, mes]);

    useEffect(() => {
        cerrarRef.current?.focus();
        const alTeclear = (e) => e.key === 'Escape' && onCerrarRef.current();
        window.addEventListener('keydown', alTeclear);
        return () => window.removeEventListener('keydown', alTeclear);
    }, []);

    const fechaMes = mesDe(mes);
    const cambiarMes = (n) => setMes(mesTexto(new Date(fechaMes.getFullYear(), fechaMes.getMonth() + n, 1)));
    const esteMes = mes === mesTexto(new Date());
    const p = datos?.persona;
    const r = datos?.resumen;
    const huecos = diaSemana(fechaMes);
    // Todos los días del mes: los que no tienen datos (futuro, antes del ingreso) quedan en blanco
    const diasDelMes = Array.from({ length: new Date(fechaMes.getFullYear(), fechaMes.getMonth() + 1, 0).getDate() },
        (_, i) => iso(new Date(fechaMes.getFullYear(), fechaMes.getMonth(), i + 1)));
    const porFecha = Object.fromEntries((datos?.dias || []).map((d) => [d.fecha, d]));
    const novedades = (datos?.dias || []).filter((d) => d.estado === 'retardo' || d.estado === 'ausente' || d.aviso
        || d.estado === 'salida_temprana' || d.extra_min > 0);

    return (
        <Flotante>
        <div className="fixed inset-0 z-50 flex justify-end" role="dialog" aria-modal="true" aria-labelledby="ficha-titulo">
            <motion.div className="absolute inset-0 bg-slate-900/30" onClick={onCerrar}
                initial={reducir ? false : { opacity: 0 }} animate={{ opacity: 1 }} />
            <motion.div
                className="relative w-full max-w-[580px] h-full bg-white shadow-2xl flex flex-col"
                initial={reducir ? false : { x: 48, opacity: 0 }} animate={{ x: 0, opacity: 1 }}
                transition={{ type: 'spring', stiffness: 420, damping: 38 }}
            >
                <div className="flex items-start gap-4 p-6 pb-5 border-b border-slate-100">
                    {p ? <Avatar persona={p} size={64} /> : <span className="w-16 h-16 rounded-full bg-slate-100" />}
                    <div className="min-w-0 flex-1">
                        <h2 id="ficha-titulo" className={`${display} text-2xl font-bold text-slate-900 truncate`}>{p?.nombre || 'Cargando...'}</h2>
                        {p && (
                            <>
                                <p className="text-sm text-slate-600 truncate">{p.cargo}</p>
                                <p className="text-sm text-slate-400 truncate">{[p.departamento, p.sede].filter(Boolean).join(', ')}</p>
                                {datos.horario && (
                                    <p className="text-xs text-slate-500 mt-1.5">Horario {datos.horario.nombre}: {resumenJornada(datos.horario.jornada)}</p>
                                )}
                            </>
                        )}
                    </div>
                    <button ref={cerrarRef} onClick={onCerrar} aria-label="Cerrar"
                        className="p-1.5 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400/60">
                        <X size={18} />
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto scroll-fino p-6 space-y-6">
                    {error && <p className="text-sm text-rose-600">{error}</p>}
                    {p?.no_marca && (
                        <p className="text-sm text-slate-600 bg-slate-50 rounded-xl px-4 py-3">
                            No marca asistencia ({p.no_marca}), así que no sale en los reportes.
                        </p>
                    )}

                    <div className="flex items-center justify-between">
                        <button type="button" onClick={() => cambiarMes(-1)} aria-label="Mes anterior"
                            className="p-2 rounded-lg text-slate-500 hover:bg-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400/60">
                            <ChevronLeft size={18} />
                        </button>
                        <p className={`${display} text-lg font-semibold text-slate-800`}>{capital(MESES[fechaMes.getMonth()])} {fechaMes.getFullYear()}</p>
                        <button type="button" onClick={() => cambiarMes(1)} disabled={esteMes} aria-label="Mes siguiente"
                            className="p-2 rounded-lg text-slate-500 hover:bg-slate-100 disabled:opacity-30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400/60">
                            <ChevronRight size={18} />
                        </button>
                    </div>

                    {!datos ? <Cargando /> : (
                        <div className={`space-y-6 transition-opacity ${datos.cargando ? 'opacity-50' : ''}`}>
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
                                <Dato titulo="Llega en promedio" valor={r.llegada_promedio ? horaAmPm(r.llegada_promedio) : 'Sin datos'}
                                    detalle={r.salida_promedio ? `Sale ${horaAmPm(r.salida_promedio)}` : null} />
                                <Dato titulo="Retardos" valor={r.retardos} tono={r.retardos ? 'text-amber-600' : 'text-slate-900'}
                                    detalle={r.retardos ? minutosTexto(r.minutos_retardo) : 'Ninguno'} />
                                <Dato titulo="Faltas" valor={r.ausencias} tono={r.ausencias ? 'text-rose-600' : 'text-slate-900'}
                                    detalle={r.justificados ? `${r.justificados} justificadas aparte` : null} />
                                <Dato titulo="Horas" valor={horas(r.horas)}
                                    detalle={`de ${horas(r.programadas)} del horario${r.extra ? `, y ${horas(r.extra)} extra aparte` : ''}`} />
                            </div>

                            {/* Calendario del mes */}
                            <div>
                                <div className="grid grid-cols-7 gap-1.5 mb-1.5">
                                    {DIAS_LETRA.map((l, i) => <span key={i} className="text-center text-[11px] font-medium text-slate-400">{l}</span>)}
                                </div>
                                <div className="grid grid-cols-7 gap-1.5">
                                    {Array.from({ length: huecos }, (_, i) => <span key={`h${i}`} />)}
                                    {diasDelMes.map((f) => (porFecha[f] ? <Dia key={f} dia={porFecha[f]} /> : (
                                        <div key={f} className="h-[74px] rounded-lg border border-dashed border-slate-200 p-1.5">
                                            <span className="text-[11px] tabular-nums text-slate-300">{Number(f.slice(8))}</span>
                                        </div>
                                    )))}
                                </div>
                            </div>

                            {datos.ausencias.length > 0 && (
                                <div>
                                    <h3 className="text-sm font-semibold text-slate-900 mb-2">Vacaciones y permisos</h3>
                                    <ul className="space-y-1.5">
                                        {datos.ausencias.map((a) => (
                                            <li key={a.codigo} className="flex items-baseline justify-between gap-3 text-sm">
                                                <span className="text-slate-700">
                                                    {a.titulo}{' '}
                                                    {a.hora_inicio
                                                        ? `el ${fechaCorta(a.desde)}, de ${horaAmPm(a.hora_inicio)} a ${horaAmPm(a.hora_fin)}`
                                                        : a.desde === a.hasta ? `el ${fechaCorta(a.desde)}` : `del ${fechaCorta(a.desde)} al ${fechaCorta(a.hasta)}`}
                                                </span>
                                                <span className={`text-xs ${a.aprobada ? 'text-emerald-600' : 'text-amber-600'}`}>{a.estado}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}

                            {novedades.length > 0 && (
                                <div>
                                    <h3 className="text-sm font-semibold text-slate-900 mb-2">Novedades del mes</h3>
                                    <ul className="divide-y divide-slate-100">
                                        {novedades.map((d) => (
                                            <li key={d.fecha} className="flex items-baseline justify-between gap-3 py-2 text-sm">
                                                <span className="text-slate-600">{fechaLarga(d.fecha)}</span>
                                                <span className={`text-right ${ESTADOS[d.estado]?.texto || 'text-slate-600'}`}>
                                                    {d.estado === 'ausente' && 'Faltó'}
                                                    {d.estado === 'retardo' && `Llegó ${horaAmPm(d.entrada)}, ${minutosTexto(d.retardo_min)} tarde`}
                                                    {d.estado === 'salida_temprana' && `Salió ${horaAmPm(d.salida)}, ${minutosTexto(d.salida_temprana_min)} antes`}
                                                    {d.extra_min > 0 && (
                                                        <span className="block text-cyan-700">
                                                            {d.estado === 'extra' ? 'Trabajó un día libre: ' : `Salió ${horaAmPm(d.salida)}, `}
                                                            {minutosTexto(d.extra_min)} extra
                                                        </span>
                                                    )}
                                                    {d.aviso && <span className="block text-xs text-rose-600">{d.aviso}</span>}
                                                    {puedeJustificar && justificacion(d) && (
                                                        <button
                                                            type="button"
                                                            onClick={() => justificar(d)}
                                                            className="mt-0.5 text-xs font-medium text-sura-blue hover:underline"
                                                        >
                                                            Justificar con un permiso
                                                        </button>
                                                    )}
                                                </span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </motion.div>
        </div>
        </Flotante>
    );
};

export default FichaPersona;
